import React from "react";

const SearchFilters = ({
  activeTab,
  searchTerm,
  setSearchTerm,
  specialtyFilter,
  setSpecialtyFilter,
  availableSpecialties = [],
  userLocation,
  nearbyRadius,
  setNearbyRadius,
  resultsCount = 0,
  onClearFilters,
  headerOffset = 64 // height of your header
}) => {
  // Hooks must be unconditional
  const [stuck, setStuck] = React.useState(false);
  const sentryRef = React.useRef(null);

  React.useEffect(() => {
    if (!sentryRef.current) return;
    const io = new IntersectionObserver(
      ([entry]) => setStuck(!entry.isIntersecting),
      { rootMargin: `-${headerOffset + 1}px 0px 0px 0px`, threshold: [1] }
    );
    io.observe(sentryRef.current);
    return () => io.disconnect();
  }, [headerOffset]);

  const show = activeTab === "explore" || activeTab === "map";
  if (!show) return null;

  const hasFilters =
    !!searchTerm || !!specialtyFilter || (userLocation && nearbyRadius < 999);

  return (
    <>
      <div ref={sentryRef} style={{ height: 1 }} />

      {/* Sticky toolbar */}
      <div
        style={{
          position: "sticky",
          top: headerOffset,
          zIndex: 90,
          background: "#fff",
          borderBottom: "1px solid #EDF2F7",
          boxShadow: stuck ? "0 8px 18px rgba(2,6,23,.06)" : "none"
        }}
      >
        <div
          style={{
            maxWidth: 800,
            margin: "0 auto",
            padding: "8px 12px"
          }}
        >
          {/* Controls row */}
          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              alignItems: "center"
            }}
          >
            <SearchInput
              placeholder={
                activeTab === "map"
                  ? "Search doctors, specialties, locations"
                  : "Search posts, doctors, topics"
              }
              value={searchTerm}
              onChange={setSearchTerm}
            />

            <MinimalSelect
              value={specialtyFilter}
              onChange={setSpecialtyFilter}
              options={[
                { label: "All specialties", value: "" },
                ...availableSpecialties.map((s) => ({ label: s, value: s }))
              ]}
              ariaLabel="Filter by specialty"
              minWidth={160}
            />

            {activeTab === "map" && userLocation && (
              <MinimalSelect
                value={nearbyRadius}
                onChange={(v) => setNearbyRadius(Number(v))}
                options={[
                  { label: "5 km", value: 5 },
                  { label: "10 km", value: 10 },
                  { label: "25 km", value: 25 },
                  { label: "50 km", value: 50 },
                  { label: "100 km", value: 100 },
                  { label: "Show all", value: 999 }
                ]}
                ariaLabel="Nearby radius"
                minWidth={120}
              />
            )}

            {/* Right-aligned actions */}
            <div style={{ flex: 1 }} />
            {hasFilters && (
              <button onClick={onClearFilters} style={resetBtnStyle}>
                Reset
              </button>
            )}
          </div>

          {/* Chips summary (compact, inline) */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
            <Chip>{resultsCount} {activeTab === "map" ? "doctors" : "posts"}</Chip>
            {searchTerm ? <Chip>“{searchTerm}”</Chip> : null}
            {specialtyFilter ? <Chip>{specialtyFilter}</Chip> : null}
            {activeTab === "map" && userLocation && nearbyRadius < 999 ? (
              <Chip>{nearbyRadius} km</Chip>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
};

/* ---------- primitives (minimal, consistent height ~40px) ---------- */

const fieldBase = {
  height: 40,
  padding: "0 12px",
  border: "1px solid #E5E7EB",
  borderRadius: 10,
  fontSize: 14,
  background: "#fff",
  color: "#111827",
  outline: "none",
  transition: "border-color .15s ease, box-shadow .15s ease"
};

const focusRing = (f) => ({
  borderColor: f ? "#111827" : "#E5E7EB",
  boxShadow: f ? "0 0 0 3px rgba(17,24,39,.06)" : "none"
});

const SearchInput = ({ placeholder, value, onChange }) => {
  const [f, setF] = React.useState(false);
  return (
    <div style={{ position: "relative", flex: 1, minWidth: 220 }}>
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setF(true)}
        onBlur={() => setF(false)}
        style={{ width: "100%", ...fieldBase, ...focusRing(f) }}
        aria-label="Search"
      />
      {/* loupe icon */}
      <span
        aria-hidden
        style={{
          position: "absolute",
          right: 10,
          top: 0,
          height: 40,
          display: "grid",
          placeItems: "center",
          color: "#9CA3AF",
          fontSize: 16
        }}
      >
        🔎
      </span>
    </div>
  );
};

const MinimalSelect = ({ value, onChange, options, ariaLabel, minWidth = 160 }) => {
  const [f, setF] = React.useState(false);
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onFocus={() => setF(true)}
      onBlur={() => setF(false)}
      aria-label={ariaLabel}
      style={{
        ...fieldBase,
        ...focusRing(f),
        minWidth,
        paddingRight: 28,
        appearance: "none",
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16'><path d='M4 6l4 4 4-4' fill='none' stroke='%239CA3AF' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/></svg>\")",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "right 8px center",
        backgroundSize: 16
      }}
    >
      {options.map((o) => (
        <option key={String(o.value)} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
};

const resetBtnStyle = {
  height: 40,
  padding: "0 12px",
  borderRadius: 10,
  border: "1px solid #E5E7EB",
  background: "#fff",
  color: "#111827",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer"
};

const Chip = ({ children }) => (
  <span
    style={{
      padding: "6px 10px",
      borderRadius: 999,
      border: "1px solid #E5E7EB",
      background: "#F8FAFC",
      fontSize: 12,
      color: "#111827",
      userSelect: "none"
    }}
  >
    {children}
  </span>
);

export default SearchFilters;
